import type { Cliente } from "@base/contracts";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { ClientesGateway } from "../infrastructure/clientes.gateway";

const ContextoDoGateway = createContext<ClientesGateway | null>(null);

export const chavesDeClientes = {
  todos: ["clientes"] as const,
  lista: ["clientes", "lista"] as const,
  membros: (clienteId: string) => ["clientes", "membros", clienteId] as const,
};

/** Injecao do gateway: HTTP em producao, memoria no teste. */
export function ProvedorDeClientes({
  gateway,
  children,
}: {
  readonly gateway: ClientesGateway;
  readonly children: ReactNode;
}) {
  return <ContextoDoGateway.Provider value={gateway}>{children}</ContextoDoGateway.Provider>;
}

export function useClientesGateway(): ClientesGateway {
  const gateway = useContext(ContextoDoGateway);
  if (!gateway) {
    throw new Error("useClientesGateway precisa estar dentro de <ProvedorDeClientes>.");
  }
  return gateway;
}

type ClienteSelecionado = {
  readonly clientes: readonly Cliente[];
  readonly selecionado: Cliente | null;
  readonly carregando: boolean;
  readonly erro: unknown;
  selecionar(id: string): void;
};

const ContextoDaSelecao = createContext<ClienteSelecionado | null>(null);

/** Chave por pessoa: duas contas no mesmo navegador nao herdam a selecao uma da outra. */
const chaveDaSelecao = (usuarioId: string) => `monte-saude:cliente-selecionado:${usuarioId}`;

function lerSelecaoSalva(usuarioId: string): string | null {
  try {
    return window.localStorage.getItem(chaveDaSelecao(usuarioId));
  } catch {
    return null;
  }
}

function salvarSelecao(usuarioId: string, clienteId: string): void {
  try {
    window.localStorage.setItem(chaveDaSelecao(usuarioId), clienteId);
  } catch {
    // Armazenamento bloqueado (aba privada, politica do navegador): a selecao
    // so nao sobrevive a recarga. A aplicacao continua funcionando.
  }
}

/**
 * Cliente selecionado na barra lateral. A lista vem da API (so o que a pessoa
 * pode ver); a escolha fica no navegador por conveniencia. Se o cliente salvo
 * sumir da lista (acesso removido), cai para o primeiro.
 *
 * A selecao e so de interface: quem garante o isolamento e o servidor, que
 * confere o vinculo em toda rota com o id do cliente.
 */
export function ProvedorDeClienteSelecionado({
  usuarioId,
  children,
}: {
  readonly usuarioId: string;
  readonly children: ReactNode;
}) {
  const gateway = useClientesGateway();
  const lista = useQuery({ queryKey: chavesDeClientes.lista, queryFn: () => gateway.listar() });
  const [escolhido, setEscolhido] = useState<string | null>(() => lerSelecaoSalva(usuarioId));

  const selecionar = useCallback(
    (id: string) => {
      setEscolhido(id);
      salvarSelecao(usuarioId, id);
    },
    [usuarioId],
  );

  const valor = useMemo<ClienteSelecionado>(() => {
    const clientes = lista.data ?? [];
    const selecionado =
      clientes.find((cliente) => cliente.id === escolhido) ??
      clientes.find((cliente) => cliente.ativo) ??
      clientes[0] ??
      null;
    return {
      clientes,
      selecionado,
      carregando: lista.isPending,
      erro: lista.error,
      selecionar,
    };
  }, [lista.data, lista.isPending, lista.error, escolhido, selecionar]);

  return <ContextoDaSelecao.Provider value={valor}>{children}</ContextoDaSelecao.Provider>;
}

export function useClienteSelecionado(): ClienteSelecionado {
  const valor = useContext(ContextoDaSelecao);
  if (!valor) {
    throw new Error("useClienteSelecionado precisa estar dentro de <ProvedorDeClienteSelecionado>.");
  }
  return valor;
}
